import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon, SearchIcon } from 'lucide-react'
import { useMemo } from 'react'
import { ProductImage } from '@app/components/product-image'
import { ScreenHeader } from '@app/components/screen-header'
import { Badge } from '@app/components/ui/badge'
import { Button } from '@app/components/ui/button'
import { Card, CardFooter } from '@app/components/ui/card'
import { Input } from '@app/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@app/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumCell,
  TableRow,
  TableScroller,
} from '@app/components/ui/table'
import {
  fetchStockList,
  formatNumber,
  quantityColor,
  shortLocationName,
  stockImageKind,
  STOCK_PAGE_SIZE,
  stockKeys,
  type StockListItem,
  type StockSort,
} from '@app/features/stok/api'
import { EmptyState, ErrorState, TableSkeleton } from '@app/features/stok/ui'
import { cn } from '@app/lib/cn'
import { useCsvDownload } from '@app/lib/download'
import { useUrlSearchInput } from '@app/lib/nav'

/**
 * Stock list — the inverse of Korgun's "configure first, then look" pattern:
 * every active card arrives the moment the screen opens, filters only narrow
 * it down.
 *
 * Solves the three core problems of Korgun's Stok Listesi screen: resolved
 * names instead of raw codes, search across code + name + barcode at once
 * instead of a single field, and a visible warehouse filter instead of the
 * hidden "active location" mode.
 */

const route = getRouteApi('/_korumali/stok')

const ALL_LOCATIONS = 'hepsi'

/**
 * Column priority.
 *
 * The shell keeps a 224px sidebar from `md` up, so the table gets about 570px
 * at 834 (iPad portrait) and 936px at 1200 (the office laptops). Code, name,
 * total and the critical/negative flag are the screen and never leave. The
 * warehouse breakdown returns at 1140 — it is the second reason to open this
 * list, but it is also the column that costs the most, and under a warehouse
 * filter it is a constant.
 */
const COLUMN = {
  group: 'hidden lg:table-cell',
  type: 'hidden 2xl:table-cell',
  location: 'hidden min-[1140px]:table-cell',
} as const

export function StockScreen() {
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const csv = useCsvDownload()

  const query = search.query ?? ''
  const location = search.location ?? ALL_LOCATIONS
  const onlyCritical = search.onlyCritical ?? false
  const sort = search.sort ?? 'stock'
  const page = search.page ?? 1

  /** Writes filters and returns to page 1 — page 7 of the old filter is meaningless. */
  // Filter clicks keep the viewport still and carry any text waiting on the
  // search debounce — a click inside the 300 ms window used to discard it.
  function setFilters(next: Partial<typeof search>) {
    const text = flushSearch({ commit: false })
    void navigate({
      search: (prev) => ({
        ...prev,
        query: text === '' ? undefined : text,
        ...next,
        page: undefined,
      }),
      resetScroll: false,
    })
  }

  /** Page 1 is the default, so it leaves the URL entirely. */
  function goToPage(next: number) {
    void navigate({ search: (prev) => ({ ...prev, page: next <= 1 ? undefined : next }) })
  }

  /*
   * Search while typing: the box keeps its own copy, and the URL is rewritten
   * 300 ms after the user stops — `replace`, so a long search doesn't bury the
   * previous screen in history.
   */
  const [searchInput, setSearchInput, flushSearch] = useUrlSearchInput(query, (text) => {
    void navigate({
      search: (prev) => ({ ...prev, query: text === '' ? undefined : text, page: undefined }),
      replace: true,
      // The commit fires mid-typing — a scroll reset would yank the viewport
      // away from the box (see the üretim list for the full story).
      resetScroll: false,
    })
  })

  const params = useMemo(
    () => ({
      query,
      location: location === ALL_LOCATIONS ? '' : location,
      onlyCritical,
      sort,
      page,
      pageSize: STOCK_PAGE_SIZE,
    }),
    [query, location, onlyCritical, sort, page],
  )

  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    queryKey: stockKeys.list(params),
    queryFn: () => fetchStockList(params),
    placeholderData: keepPreviousData,
  })

  const pagination = data?.pagination
  const firstRow = pagination && pagination.total > 0 ? (pagination.page - 1) * pagination.pageSize + 1 : 0
  const lastRow = pagination ? Math.min(pagination.page * pagination.pageSize, pagination.total) : 0

  return (
    <>
      <ScreenHeader
        title="Stok mevcudu"
        description={
          pagination
            ? `${formatNumber(pagination.total)} aktif stok kartı. Eksi mevcut, sarf fişi girilip üretim girişi yapılmadığında oluşur.`
            : 'Stok kartı × renk × beden × depo kırılımında eldeki miktar.'
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1 sm:max-w-md">
          <SearchIcon
            className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-ink-muted"
            aria-hidden
          />
          <Input
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            className="pl-7"
            type="search"
            aria-label="Stok kodu, tanım veya barkod ara"
            placeholder="Stok kodu, tanım veya barkod ara"
          />
        </div>

        <Select
          value={location}
          onValueChange={(value) =>
            setFilters({ location: value === ALL_LOCATIONS ? undefined : value })
          }
        >
          <SelectTrigger className="w-52" aria-label="Depo filtresi">
            <SelectValue placeholder="Tüm depolar" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_LOCATIONS}>Tüm depolar</SelectItem>
            {data?.locations.map((option) => (
              <SelectItem key={option.location} value={option.location}>
                {shortLocationName(option.name)} · {formatNumber(option.cardCount)} kart
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={sort}
          onValueChange={(value) =>
            setFilters({ sort: value === 'stock' ? undefined : (value as StockSort) })
          }
        >
          <SelectTrigger className="w-48" aria-label="Sıralama">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {/*
             * Wording checked against the server's actual ordering: the default
             * sort puts cards with a POSITIVE on-hand first, then the rest by
             * code. On the mirror that is 284 cards with stock, ahead of 457
             * negative and 1.327 idle ones — so "önce mevcudu olanlar" is
             * literally what the first page shows. (It was not, while the sort
             * keyed on `total <> 0`: minus balances counted as "mevcut" and
             * filled page one.)
             */}
            <SelectItem value="stock">Önce mevcudu olanlar</SelectItem>
            <SelectItem value="code">Stok koduna göre</SelectItem>
            <SelectItem value="quantity">Mevcuda göre (çoktan aza)</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant={onlyCritical ? 'primary' : 'outline'}
          aria-pressed={onlyCritical}
          onClick={() => setFilters({ onlyCritical: onlyCritical ? undefined : true })}
        >
          Kritik seviyenin altı
        </Button>

        {/* Exports the whole filtered list, not just the page on screen. */}
        <Button
          variant="outline"
          disabled={csv.busy}
          aria-busy={csv.busy}
          onClick={() =>
            csv.start('/stock/export', {
              query: flushSearch(),
              location: location === ALL_LOCATIONS ? '' : location,
              onlyCritical,
              sort,
            })
          }
        >
          <DownloadIcon aria-hidden />
          {csv.busy ? 'Hazırlanıyor…' : 'Dışa aktar'}
        </Button>

        {csv.error ? (
          <span role="alert" className="text-micro text-hardal">
            {csv.error}
          </span>
        ) : null}

        <span
          aria-live="polite"
          className="ml-auto text-micro text-ink-muted tabular-nums"
        >
          {isFetching && !isPending ? 'Yenileniyor…' : pagination ? `${formatNumber(firstRow)}–${formatNumber(lastRow)} / ${formatNumber(pagination.total)}` : ''}
        </span>
      </div>

      <Card>
        {isPending ? (
          <TableSkeleton rows={10} columns={6} />
        ) : isError ? (
          <ErrorState
            message={error instanceof Error ? error.message : 'Stok listesi yüklenemedi.'}
            onRetry={() => void refetch()}
          />
        ) : data.items.length === 0 ? (
          <EmptyList
            search={query}
            onlyCritical={onlyCritical}
            definedCardCount={data.definedCardCount}
            clearFilters={() => {
              // The URL is the single writer; the search box follows it.
              void navigate({
                search: (prev) => ({
                  ...prev,
                  query: undefined,
                  location: undefined,
                  onlyCritical: undefined,
                  sort: undefined,
                  page: undefined,
                }),
                resetScroll: false,
              })
            }}
          />
        ) : (
          <TableScroller className="max-h-[calc(100dvh-16rem)]">
            <Table>
              <TableHeader>
                <TableRow>
                  {/* The photo column has no heading: the pictures are the label. */}
                  <TableHead className="w-9 pr-0" />
                  <TableHead className="w-40">Stok kodu</TableHead>
                  <TableHead className="min-w-40">Tanım</TableHead>
                  <TableHead className={cn('w-36', COLUMN.group)}>Grup</TableHead>
                  <TableHead className={cn('w-24', COLUMN.type)}>Tür</TableHead>
                  <TableHead className={cn('w-52', COLUMN.location)}>Depo dağılımı</TableHead>
                  <TableHead className="w-26 text-right">Toplam</TableHead>
                  <TableHead className="w-20">Durum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((item) => (
                  <StockRow key={item.skod} item={item} />
                ))}
              </TableBody>
            </Table>
          </TableScroller>
        )}

        {pagination && pagination.totalPages > 1 ? (
          <CardFooter className="justify-between">
            <span className="tabular-nums">
              Sayfa {formatNumber(pagination.page)} / {formatNumber(pagination.totalPages)}
            </span>
            <span className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => goToPage(page - 1)}
              >
                <ChevronLeftIcon aria-hidden />
                Önceki
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => goToPage(page + 1)}
              >
                Sonraki
                <ChevronRightIcon aria-hidden />
              </Button>
            </span>
          </CardFooter>
        ) : null}
      </Card>
    </>
  )
}

function StockRow({ item }: { item: StockListItem }) {
  return (
    // The whole row is clickable: the link spreads outside the cell, giving a
    // normal list → detail navigation instead of Korgun's record arrows.
    <TableRow className="relative">
      {/*
       * No link of its own: the row's overlay anchor below is absolutely
       * positioned against the row and paints over this cell, so the photo is
       * part of the same single target instead of a second, nested one.
       */}
      <TableCell className="pr-0">
        <ProductImage
          imageKey={item.imageKey}
          alt={`${item.name ?? item.skod} fotoğrafı`}
          kind={stockImageKind(item.shape)}
        />
      </TableCell>
      <TableCell className="whitespace-nowrap font-mono">
        <Link
          to="/stok/$skod"
          params={{ skod: item.skod }}
          className="text-ink after:absolute after:inset-0 hover:text-kordovan"
        >
          {item.skod}
        </Link>
      </TableCell>
      <TableCell className="max-w-0 truncate" title={item.name ?? undefined}>
        {item.name ?? '—'}
      </TableCell>
      <TableCell className={cn('truncate text-ink-muted', COLUMN.group)}>
        {item.groupName ?? item.groupCode ?? '—'}
      </TableCell>
      <TableCell className={cn('text-ink-muted', COLUMN.type)}>
        {item.shapeName ?? '—'}
      </TableCell>
      <TableCell className={COLUMN.location}>
        {item.locations.length === 0 ? (
          <span className="text-ink-muted/55">Hareket yok</span>
        ) : (
          <span className="flex flex-wrap gap-x-3 gap-y-0.5">
            {item.locations.map((location) => (
              <span key={location.location} className="whitespace-nowrap">
                <span className="text-micro uppercase tracking-[0.04em] text-ink-muted">
                  {shortLocationName(location.name)}
                </span>{' '}
                <span className={cn('font-mono', quantityColor(location.quantity))}>
                  {formatNumber(location.quantity)}
                </span>
              </span>
            ))}
          </span>
        )}
      </TableCell>
      <TableNumCell className={cn('font-medium', quantityColor(item.total))}>
        {formatNumber(item.total)}
      </TableNumCell>
      <TableCell>
        {item.critical ? (
          <Badge tone="warn">Kritik</Badge>
        ) : item.total < 0 ? (
          <Badge tone="warn">Eksi</Badge>
        ) : (
          <span className="text-ink-muted/55">—</span>
        )}
      </TableCell>
    </TableRow>
  )
}

function EmptyList({
  search,
  onlyCritical,
  definedCardCount,
  clearFilters,
}: {
  search: string
  onlyCritical: boolean
  definedCardCount: number
  clearFilters: () => void
}) {
  if (onlyCritical && definedCardCount === 0) {
    return (
      <EmptyState
        title="Kritik seviye tanımlı kart yok"
        description="Korgun'da hiçbir stok kartına kritik seviye girilmemiş, bu yüzden bu filtre boş kalıyor. Seviyeler Korgun'da tanımlandığında liste kendiliğinden dolar."
        action={
          <Button variant="outline" size="sm" onClick={clearFilters}>
            Filtreleri temizle
          </Button>
        }
      />
    )
  }

  return (
    <EmptyState
      title={search ? 'Eşleşen stok kartı yok' : 'Bu filtrelerde stok kartı yok'}
      description={
        search
          ? `"${search}" için kod, tanım ve barkodlarda karşılık bulunamadı. Barkodun tamamını ya da kodun ilk hanelerini deneyin.`
          : 'Seçili depoda aktif kart bulunmuyor. Depo seçimini genişletin.'
      }
      action={
        <Button variant="outline" size="sm" onClick={clearFilters}>
          Filtreleri temizle
        </Button>
      }
    />
  )
}
